import { useEffect } from "react";

interface UseBlurDetectionOptions {
  isOpen: boolean;
  onClose: () => void;
  sidebarRef: React.RefObject<HTMLDivElement>;
  isResizing?: boolean;
  enableEscapeKey?: boolean;
  enableClickOutside?: boolean;
  ignoreSelectors?: string[];
}

const DEFAULT_IGNORE_SELECTORS = [
  "[data-column-header]",
  "[data-radix-portal]",
  "[data-radix-dialog-content]",
  "[data-radix-dialog-overlay]",
  "[data-radix-popover-content]",
  "[data-radix-dropdown-menu-content]",
  "[data-radix-tooltip-content]",
  "[data-radix-select-content]",
  "[data-radix-context-menu-content]",
  "[role='dialog']",
  "[role='menu']",
  "[role='listbox']",
  "[role='tooltip']",
];

const useBlurDetection = ({
  isOpen,
  onClose,
  sidebarRef,
  isResizing = false,
  enableEscapeKey = true,
  enableClickOutside = true,
  ignoreSelectors = [],
}: UseBlurDetectionOptions) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const allIgnoreSelectors = [...DEFAULT_IGNORE_SELECTORS, ...ignoreSelectors];

    const handleClickOutside = (event: MouseEvent) => {
      if (!enableClickOutside || !event.target || isResizing || !sidebarRef.current) {
        return;
      }

      const target = event.target as HTMLElement;

      // Check if click is on any ignored element
      for (const selector of allIgnoreSelectors) {
        if (target.closest(selector)) {
          return;
        }
      }

      // Check if there's any open dialog that should prevent blur detection
      const hasOpenDialog =
        document.querySelector("[data-radix-dialog-content]") ||
        document.querySelector("[role='dialog']");

      if (hasOpenDialog) {
        return;
      }

      // Get the sidebar's bounding rectangle
      const sidebarRect = sidebarRef.current.getBoundingClientRect();

      // Check if click is outside the sidebar's boundaries
      const clickX = event.clientX;
      const clickY = event.clientY;

      const isOutsideSidebar =
        clickX < sidebarRect.left ||
        clickX > sidebarRect.right ||
        clickY < sidebarRect.top ||
        clickY > sidebarRect.bottom;

      if (isOutsideSidebar) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!enableEscapeKey || event.key !== "Escape") {
        return;
      }

      // Check if there's any open Radix component that should handle Escape
      const hasOpenRadixComponent =
        document.querySelector("[data-radix-dialog-content]") ||
        document.querySelector("[data-radix-popover-content]") ||
        document.querySelector("[data-radix-dropdown-menu-content]") ||
        document.querySelector("[data-radix-select-content]") ||
        document.querySelector("[data-radix-context-menu-content]") ||
        document.querySelector("[role='dialog']") ||
        document.querySelector("[role='menu']") ||
        document.querySelector("[role='listbox']");

      if (!hasOpenRadixComponent) {
        onClose();
      }
    };

    // Small delay to prevent immediate closing when opening
    const timeoutId = setTimeout(() => {
      if (enableClickOutside) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      if (enableEscapeKey) {
        document.addEventListener("keydown", handleKeyDown);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (enableClickOutside) {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      if (enableEscapeKey) {
        document.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [
    isOpen,
    isResizing,
    onClose,
    sidebarRef,
    enableEscapeKey,
    enableClickOutside,
    ignoreSelectors,
  ]);
};

export default useBlurDetection;
